import React, { useState, useEffect } from 'react';
import {
    VStack,
    Box,
    Heading,
    Text,
    Button,
    Flex,
    HStack,
    Badge,
    Icon,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    useToast,
    IconButton,
    InputGroup,
    InputLeftElement,
    Input,
    Select,
    Spacer
} from '@chakra-ui/react';
import { AddIcon, EditIcon, SearchIcon, QuestionIcon } from '@chakra-ui/icons';
import {
    FaEnvelope,
    FaAddressBook,
    FaFileAlt,
    FaUserCog,
    FaUserShield,
} from 'react-icons/fa';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import ReleaseFormModal from './ReleaseFormModal';
import HelpDialog from '../HelpDialog';

const ReleaseLog = ({ isAdminView = false }) => {
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [showHelp, setShowHelp] = useState(false);
    const supabase = useSupabaseClient();
    const toast = useToast();

    useEffect(() => {
        fetchReleases();
    }, []);

    const fetchReleases = async () => {
        try {
            setLoading(true);
            
            const { data: releasesData, error: releasesError } = await supabase
                .from('releases')
                .select(`
                    *,
                    changelog_entries:changelog_entries (
                        *,
                        category:changelog_categories (
                            id,
                            name,
                            icon,
                            display_order
                        )
                    )
                `)
                .order('version_major', { ascending: false })
                .order('version_minor', { ascending: false })
                .order('version_patch', { ascending: false });

            if (releasesError) throw releasesError;

            setReleases(releasesData || []);
        } catch (err) {
            console.error('Error in fetchReleases:', err);
            setError(err.message);
            toast({
                title: 'Error fetching releases',
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const getCategoryIcon = (iconName) => {
        const icons = {
            FaEnvelope,
            FaAddressBook,
            FaFileAlt,
            FaUserCog,
            FaUserShield,
        };
        return icons[iconName] || FaEnvelope;
    };

    const getChangeTypeColor = (type) => {
        const colors = {
            'MAJOR': 'red',
            'MINOR': 'blue',
            'PATCH': 'green'
        };
        return colors[type] || 'gray';
    };

    const getImpactColor = (impact) => {
        const colors = {
            'HIGH': 'red',
            'MEDIUM': 'yellow',
            'LOW': 'green'
        };
        return colors[impact] || 'gray';
    };

    const categorizedChanges = (entries) => {
        const categories = {};
        entries?.forEach(entry => {
            if (!categories[entry.category.id]) {
                categories[entry.category.id] = {
                    id: entry.category.id,
                    name: entry.category.name,
                    icon: entry.category.icon,
                    displayOrder: entry.category.display_order,
                    entries: []
                };
            }
            categories[entry.category.id].entries.push(entry);
        });
        return Object.values(categories).sort((a, b) => a.displayOrder - b.displayOrder);
    };

    const handleExpandAll = (releaseId) => {
        const categories = categorizedChanges(releases.find(r => r.id === releaseId)?.changelog_entries);
        const newExpandedSections = { ...expandedSections };
        newExpandedSections[releaseId] = categories.map((_, index) => index);
        setExpandedSections(newExpandedSections);
    };

    const handleCollapseAll = (releaseId) => {
        const newExpandedSections = { ...expandedSections };
        newExpandedSections[releaseId] = [];
        setExpandedSections(newExpandedSections);
    };

    const filterReleases = (releases) => {
        let filtered = [...releases];
        
        // Apply status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter(release => release.status === statusFilter);
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const searchTerm = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(release => {
                const versionString = `v${release.version_major}.${release.version_minor}.${release.version_patch}`;
                const basicInfo = [
                    versionString,
                    release.release_name,
                    release.description,
                    new Date(release.release_date).toLocaleDateString(),
                    release.status
                ].join(' ').toLowerCase();

                if (basicInfo.includes(searchTerm)) return true;

                return release.changelog_entries?.some(entry => {
                    const categoryInfo = [
                        entry.category?.name,
                        entry.change_type,
                        entry.impact,
                        entry.description
                    ].join(' ').toLowerCase();
                    
                    return categoryInfo.includes(searchTerm);
                });
            });
        }

        return filtered;
    };

    const filteredReleases = filterReleases(releases);

    if (loading) {
        return (
            <Box p={6}>
                <Text>Loading releases...</Text>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={6}>
                <Text color="red.500">Error: {error}</Text>
            </Box>
        );
    }

    return (
        <VStack spacing={8} width="100%" p={6} align="stretch">
            {/* Page Header */}
            <Flex direction="column" gap={4}>
                {/* Title Row */}
                <Flex align="center">
                    <Heading size="lg">
                        {isAdminView ? 'Manage Releases' : 'View Releases'}
                    </Heading>
                    {isAdminView && (
                        <IconButton
                            icon={<AddIcon />}
                            colorScheme="blue"
                            aria-label="Add Release"
                            onClick={() => {
                                setSelectedRelease(null);
                                setIsReleaseModalOpen(true);
                            }}
                            ml="100px"
                        />
                    )}
                    <Spacer />
                    <IconButton
                        icon={<QuestionIcon />}
                        variant="ghost"
                        aria-label="Help"
                        onClick={() => setShowHelp(true)}
                    />
                </Flex>

                {/* Search and Filter Row */}
                <Flex justify="space-between" align="center" gap={4}>
                    <InputGroup maxW="600px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search by version, name, description, or changelog entries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        width="200px"
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="PLANNED">Planned</option>
                        <option value="IN_DEVELOPMENT">In Development</option>
                        <option value="READY">Ready</option>
                        <option value="PUBLISHED">Published</option>
                        <option value="DEPRECATED">Deprecated</option>
                    </Select>
                </Flex>
            </Flex>

            {/* Release Timeline */}
            <VStack spacing={6} width="100%" align="stretch">
                {filteredReleases.length === 0 ? (
                    <Box p={6} textAlign="center">
                        <Text color="gray.600">
                            {searchQuery || statusFilter !== 'ALL'
                                ? 'No releases match your search criteria'
                                : 'No releases available'}
                        </Text>
                    </Box>
                ) : (
                    filteredReleases.map((release) => (
                        <Box 
                            key={release.id}
                            borderWidth="1px"
                            borderRadius="lg"
                            p={6}
                            bg="white"
                            shadow="sm"
                        >
                            {/* Release Header */}
                            <Flex justify="space-between" align="center" mb={4}>
                                <VStack align="start" spacing={1}>
                                    <HStack>
                                        <Heading size="md">
                                            v{release.version_major}.{release.version_minor}.{release.version_patch}
                                        </Heading>
                                        <Text color="gray.500">
                                            {release.release_name}
                                        </Text>
                                        {isAdminView && (
                                            <Badge colorScheme={release.status === 'PUBLISHED' ? 'green' : 'blue'}>
                                                {release.status}
                                            </Badge>
                                        )}
                                    </HStack>
                                    <Text color="gray.600">
                                        Released on {new Date(release.release_date).toLocaleDateString()}
                                    </Text>
                                </VStack>
                                <HStack spacing={2}>
                                    <Button
                                        size="sm"
                                        onClick={() => handleExpandAll(release.id)}
                                    >
                                        Expand All
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => handleCollapseAll(release.id)}
                                    >
                                        Collapse All
                                    </Button>
                                    {isAdminView && (
                                        <IconButton
                                            icon={<EditIcon />}
                                            aria-label="Edit release"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedRelease(release);
                                                setIsReleaseModalOpen(true);
                                            }}
                                        />
                                    )}
                                </HStack>
                            </Flex>

                            <Text mb={4}>{release.description}</Text>

                            {/* Changelog Categories */}
                            <Accordion
                                allowMultiple
                                index={expandedSections[release.id] || []}
                                onChange={(expandedIndexes) => {
                                    setExpandedSections({
                                        ...expandedSections,
                                        [release.id]: expandedIndexes
                                    });
                                }}
                            >
                                {categorizedChanges(release.changelog_entries).map((category, index) => (
                                    <AccordionItem key={category.id}>
                                        <AccordionButton>
                                            <HStack flex="1">
                                                <Icon as={getCategoryIcon(category.icon)} />
                                                <Text fontWeight="semibold">
                                                    {category.name}
                                                </Text>
                                                <Badge>{category.entries.length}</Badge>
                                            </HStack>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel>
                                            <VStack align="stretch" spacing={2}>
                                                {category.entries.map((entry) => (
                                                    <Box
                                                        key={entry.id}
                                                        p={2}
                                                        borderRadius="md"
                                                        bg="gray.50"
                                                    >
                                                        <HStack spacing={2} mb={1}>
                                                            <Badge colorScheme={getChangeTypeColor(entry.change_type)}>
                                                                {entry.change_type}
                                                            </Badge>
                                                            {entry.impact && (
                                                                <Badge colorScheme={getImpactColor(entry.impact)}>
                                                                    {entry.impact} Impact
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                        <Text>{entry.description}</Text>
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </AccordionPanel>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </Box>
                    ))
                )}
            </VStack>

            {isAdminView && (
                <ReleaseFormModal
                    isOpen={isReleaseModalOpen}
                    onClose={() => {
                        setIsReleaseModalOpen(false);
                        setSelectedRelease(null);
                    }}
                    editRelease={selectedRelease}
                    onSuccess={() => {
                        setIsReleaseModalOpen(false);
                        setSelectedRelease(null);
                        fetchReleases();
                    }}
                />
            )}

            <HelpDialog 
                isOpen={showHelp} 
                onClose={() => setShowHelp(false)}
                section="releases"
            />
        </VStack>
    );
};

export default ReleaseLog;
